import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
// What everyone paid before local prices (config/dollarPricing.js): the fallback
import { DOLLARS } from '../config/dollarPricing';
import { formatExactForPage, formatForPage, offerLabel } from '../config/localPrices';
import { useLocale } from '../i18n/LocaleContext';
import { formatExact, formatMoney } from '../utils/money';

/**
 * What a bundle costs where this visitor is (GET /subscription/pricing, which
 * reads Cloudflare's country), with the price id to check out with. Falls back
 * to dollars, which is what everyone paid until now, so a failed request never
 * leaves the page without a price.
 *
 * Every amount it writes is in the page's language's style (config/localPrices.js
 * formatForPage: "R$19,90" on a Portuguese page, "5,99 US$" on a French one), the
 * same as the growth pages, so one screen never shows a price in two styles. A
 * country's own dollar price (Egypt, with the pounds beside it) is written as the
 * API writes it, everywhere on the screen.
 */
export function useLocalPricing() {
  const { locale } = useLocale();
  const { data } = useQuery({
    queryKey: ['local-pricing'],
    queryFn: api.getLocalPricing,
    staleTime: 60 * 60 * 1000,
    retry: 1,
    placeholderData: DOLLARS,
  });
  const pricing = data || DOLLARS;
  const regional = pricing.monthly?.local_amount != null;
  const write = (currency, amount) => (regional ? formatMoney(currency, amount) : formatForPage(currency, amount, locale));
  return {
    ...pricing,
    /** `amount` in this visitor's currency, written the way the page's language writes it */
    format: (amount) => write(pricing.currency, amount),
    /** `amount` in another currency (a pack priced in dollars beside a local Pro) */
    formatIn: (currency, amount) => write(currency || pricing.currency, amount),
    /** `amount` to the cent ("MX$79.50", a half first month) */
    exact: (amount) => (regional ? formatExact(pricing.currency, amount) : formatExactForPage(pricing.currency, amount, locale)),
    /** An offer of this answer (the pack, the clip pack, `monthly`, `yearly`) as the page shows it */
    label: (offer) => offerLabel({ ...offer, currency: offer?.currency || pricing.currency }, locale),
    /** The offer's label as the API sends it, for analytics, which keep the strings they always had */
    apiLabel: (offer) => offer?.label || formatMoney(offer?.currency || pricing.currency, offer?.amount),
  };
}
